/**
 * Utility functions for time zone handling
 */
import { simpleDebugLog } from './simpleDebugLogger';

// Map of UTC offsets to common time zone abbreviations and full names
// This is a simplified mapping - in a real app, you might use a more comprehensive library
interface TimeZoneInfo {
  abbreviation: string;
  fullName: string;
}

// Since it's May 2025, we're in Daylight Saving Time for North America
const TIME_ZONE_MAP: Record<string, TimeZoneInfo> = {
  'UTC-12': { abbreviation: 'BIT', fullName: 'Baker Island Time' },
  'UTC-11': { abbreviation: 'SST', fullName: 'Samoa Standard Time' },
  'UTC-10': { abbreviation: 'HST', fullName: 'Hawaii-Aleutian Standard Time' },
  'UTC-9': { abbreviation: 'AKDT', fullName: 'Alaska Daylight Time' },
  'UTC-8': { abbreviation: 'AKST', fullName: 'Alaska Standard Time' },
  'UTC-7': { abbreviation: 'PDT', fullName: 'Pacific Daylight Time' },
  'UTC-6': { abbreviation: 'MDT', fullName: 'Mountain Daylight Time' },
  'UTC-5': { abbreviation: 'CDT', fullName: 'Central Daylight Time' },
  'UTC-4': { abbreviation: 'EDT', fullName: 'Eastern Daylight Time' },
  'UTC-3': { abbreviation: 'ADT', fullName: 'Atlantic Daylight Time' },
  'UTC-2': { abbreviation: 'BRST', fullName: 'Brazil Standard Time' },
  'UTC-1': { abbreviation: 'CVT', fullName: 'Cape Verde Time' },
  'UTC+0': {
    abbreviation: 'GMT/UTC',
    fullName: 'Greenwich Mean Time / Coordinated Universal Time',
  },
  'UTC+1': { abbreviation: 'BST', fullName: 'British Summer Time' },
  'UTC+2': { abbreviation: 'CEST', fullName: 'Central European Summer Time' },
  'UTC+3': { abbreviation: 'EEST', fullName: 'Eastern European Summer Time' },
  'UTC+4': { abbreviation: 'GST', fullName: 'Gulf Standard Time' },
  'UTC+5': { abbreviation: 'PKT', fullName: 'Pakistan Standard Time' },
  'UTC+5:30': { abbreviation: 'IST', fullName: 'Indian Standard Time' },
  'UTC+6': { abbreviation: 'BST', fullName: 'Bangladesh Standard Time' },
  'UTC+7': { abbreviation: 'ICT', fullName: 'Indochina Time' },
  'UTC+8': { abbreviation: 'CST', fullName: 'China Standard Time' },
  'UTC+9': { abbreviation: 'JST', fullName: 'Japan Standard Time' },
  'UTC+10': {
    abbreviation: 'AEST',
    fullName: 'Australian Eastern Standard Time',
  },
  'UTC+11': {
    abbreviation: 'AEDT',
    fullName: 'Australian Eastern Daylight Time',
  },
  'UTC+12': { abbreviation: 'NZST', fullName: 'New Zealand Standard Time' },
  'UTC+13': { abbreviation: 'NZDT', fullName: 'New Zealand Daylight Time' },
};

/**
 * Get time zone information from a UTC offset string
 * @param utcOffset UTC offset string (e.g., 'UTC-7', 'UTC+1')
 * @returns Object with abbreviation and full name
 */
export function getTimeZoneInfo(utcOffset: string): TimeZoneInfo {
  // Handle null or undefined
  if (!utcOffset) {
    return {
      abbreviation: 'UTC',
      fullName: 'Coordinated Universal Time',
    };
  }

  // Default fallback
  const defaultInfo: TimeZoneInfo = {
    abbreviation: utcOffset,
    fullName: `${utcOffset} (Coordinated Universal Time ${utcOffset.substring(3) || ''})`,
  };

  return TIME_ZONE_MAP[utcOffset] || defaultInfo;
}

/**
 * Format a UTC offset to standard format
 * @param utcOffset UTC offset string (e.g., 'UTC-7', 'UTC+1')
 * @returns Formatted offset (e.g., '-07:00', '+01:00')
 */
export function formatUtcOffset(utcOffset: string): string {
  if (!utcOffset) {
    return '+00:00';
  }

  if (!utcOffset.startsWith('UTC')) {
    return '+00:00';
  }

  // Extract the offset part
  const offsetStr = utcOffset.substring(3);

  // Handle case where there's no offset specified
  if (!offsetStr || offsetStr.trim() === '') {
    return '+00:00';
  }

  // Determine the sign
  const sign = offsetStr.startsWith('-') ? '-' : '+';

  // Remove sign for parsing
  const numericPart = offsetStr.replace(/[+-]/, '');

  // Support offsets that include minutes, e.g. "5:30"
  const [hourPart, minutePart] = numericPart.split(':');

  const hours = parseInt(hourPart || '0', 10);
  const minutes = minutePart ? parseInt(minutePart, 10) : 0;

  const safeHours = isNaN(hours) ? 0 : hours;
  const safeMinutes = isNaN(minutes) ? 0 : minutes;

  return `${sign}${safeHours.toString().padStart(2, '0')}:${safeMinutes.toString().padStart(2, '0')}`;
}

/**
 * Get tooltip text for a time zone
 * @param utcOffset UTC offset string (e.g., 'UTC-7', 'UTC+1')
 * @returns Tooltip text with full information
 */
export function getTimeZoneTooltip(utcOffset: string): string {
  try {
    const info = getTimeZoneInfo(utcOffset || 'UTC+0');
    const formattedOffset = formatUtcOffset(utcOffset || 'UTC+0');

    return `${info.fullName}\nUTC ${formattedOffset}`;
  } catch (error) {
    simpleDebugLog.error('Error generating time zone tooltip:', error);
    return utcOffset || 'Unknown time zone';
  }
}
